import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function PlymouthCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Plymouth Pothole Claims | 37% of Residential Roads in Red | Fixtyer"
        description="Plymouth City Council's own data shows 36.64% of unclassified roads in RED condition — over a third of the city's residential network. Resurfacing fell 80% in 2023/24. See what the DfT AMBER rating means for your pothole claim."
        canonical="/councils/plymouth"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Plymouth', url: '/councils/plymouth' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Plymouth spends three times its DfT allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but your claim turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired under Section 58 — not on aggregate spending. Plymouth is AMBER overall because road condition is AMBER: B and C roads lost 5 percentage points of green condition in 2024, and over a third of unclassified roads were in RED at the last comparable survey.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'Unclassified roads make up 621.67km — 70% of Plymouth\'s 882km network — and Plymouth\'s own Gaist surveys found 36.64% in RED condition in 2020 and 34.83% in 2022. The council surveys U-roads only every three years, so there is no network-level condition data for 2021, 2023 or 2024. That survey gap is a structural argument under Section 58.',
          },
          {
            question: 'Does Plymouth\'s 40mm investigatory threshold affect my claim?',
            answer: 'It defines when Plymouth will repair a pothole — at least 40mm deep and over 300mm wide — but it does not define when the council is liable for damage. A defect below the repair threshold can still have caused your damage, and prior reports of the same location create a record of actual notice regardless of whether the pothole yet met investigatory criteria.',
          },
          {
            question: 'Plymouth says temporary pothole repairs are common — does that help my claim?',
            answer: 'It can. Plymouth publicly acknowledges that its contractor sometimes carries out temporary fixes until permanent repairs can be arranged. A temporary patch on a road already in RED condition at the last survey suggests the underlying surface failure was known — and that reactive patching did not restore the carriageway to a maintainable standard.',
          },
          {
            question: 'Why did pothole repairs peak at 3,218 in 2023/24?',
            answer: 'Plymouth filled 3,218 potholes in 2023/24 — the highest in five years — the same year carriageway resurfacing fell to just 1.17 miles, down from 6.03 miles in 2020/21. When preventative resurfacing collapses and reactive pothole filling spikes, defects are forming faster than structural renewal can keep pace — exactly the pattern where prior reports and photographic evidence decide claims.',
          },
          {
            question: 'The A38 is not Plymouth\'s responsibility — who do I claim against?',
            answer: 'Plymouth Highways does not manage the A38 Parkway or many of its slip-roads — National Highways does. If your damage was on the A38, report it to National Highways and pursue your claim against them. Plymouth\'s transparency report covers HMPE (Highways Maintainable at Public Expense) only, excluding trunk roads.',
          },
          {
            question: 'Do I need to report the pothole before claiming?',
            answer: 'Plymouth\'s own claims guidance states you should have reported the defect before submitting a claim. Reporting creates a dated council record — useful if the pothole was reported before your incident, or if the council failed to repair it within a reasonable time. Keep your reference number and any confirmation emails.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Plymouth City Council"
        councilSlug="plymouth"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/plymouth"
        headline="Plymouth Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Plymouth</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Plymouth: more than a third of residential roads in red condition
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Plymouth City Council&apos;s own Gaist surveys found{' '}
            <strong className="text-rose-400">36.64% of unclassified roads in RED condition</strong> — roughly
            228km of residential streets on a network where U-roads make up 70% of all carriageways. The DfT rates
            overall maintenance <strong className="text-amber-400">AMBER</strong>, spend{' '}
            <strong className="text-emerald-400">GREEN</strong>, and best practice{' '}
            <strong className="text-amber-400">AMBER</strong>. Meanwhile carriageway resurfacing collapsed from{' '}
            <strong className="text-rose-400">6.03 miles to 1.17 miles</strong> in 2023/24 while pothole fills
            hit a five-year high of 3,218.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">36.64%</div>
            <div className="text-white font-medium mt-2">U-roads in RED condition (2020 survey)</div>
            <div className="text-brand-300 text-sm mt-1">
              621.67km of unclassified roads — 70% of Plymouth&apos;s network — surveyed only every three years.
              The 2022 survey found 34.83% still in RED. No U-road condition data exists for 2021, 2023 or 2024.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The condition picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER survey data from Plymouth&apos;s own transparency report — classified roads
            slipping while A-roads hold steady
          </p>

          {/* Network context */}
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-brand-900">882km</div>
              <div className="text-sm text-surface-600">Total road network</div>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-rose-700">622km</div>
              <div className="text-sm text-rose-800">Unclassified roads (70%)</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-brand-900">206km</div>
              <div className="text-sm text-surface-600">B and C roads (23%)</div>
            </div>
          </div>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (54km — 6% of network): broadly stable</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">2.9%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">up from 1.8% in 2022</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">22.4%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">up from 19.2% in 2023</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">74.8%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">down from 78.9% in 2022–23</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              A-roads are surveyed 100% each year using SCANNER. Condition has drifted slightly worse since 2022,
              with green roads falling nearly four percentage points. Plymouth notes it changed SCANNER supplier
              for 2025/26, which may have affected results.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (206km — 23% of network): amber rising</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2.6%</td>
                    <td className="px-4 py-2 text-center">22.5%</td>
                    <td className="px-4 py-2 text-center">74.9%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">3.9%</td>
                    <td className="px-4 py-2 text-center">23.1%</td>
                    <td className="px-4 py-2 text-center">72.9%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">3.7%</td>
                    <td className="px-4 py-2 text-center">21.0%</td>
                    <td className="px-4 py-2 text-center">75.2%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">3.0%</td>
                    <td className="px-4 py-2 text-center">20.0%</td>
                    <td className="px-4 py-2 text-center">77.0%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">3.5%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">24.4%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">72.1%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              Amber B/C roads jumped from 20.0% to 24.4% in a single year, and green roads fell from 77.0% to
              72.1%. Over a quarter of Plymouth&apos;s B and C network now needs — or will soon need — maintenance.
              B roads are surveyed 100% annually; C roads at 50%, with coverage under review.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN spend, AMBER condition</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£4.7m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£15.0m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">80%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Plymouth projects spending more than <span className="text-emerald-400">three times</span> its DfT
              capital allocation in 2025/26 — yet road condition remains AMBER. The DfT Spend scorecard is GREEN;
              the question for your claim is whether that investment reached the specific road where you were damaged.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road blind spot */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 622km blind spot
          </h2>
          <p className="text-surface-600 mb-8">
            70% of Plymouth&apos;s network is unclassified roads — condition-surveyed only every three years
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">36.64%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Not a survey year</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">34.83%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Not a survey year</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Not a survey year</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The three-year gap</h3>
              <p className="text-surface-600 text-sm mb-3">
                Plymouth uses Gaist to visually analyse the unclassified network and aims to survey the whole
                U-road network every three years. That means for incidents in 2021, 2023 or 2024, there is{' '}
                <strong>no network-level condition data</strong> for the road type where most Plymouth pothole
                claims actually happen.
              </p>
              <p className="text-surface-600 text-sm">
                At the last two comparable surveys, roughly <strong>one in three U-roads was in RED condition</strong>{' '}
                — approximately 216–228km of residential streets, estate roads and local routes across the city.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Different ruler, different standard</h3>
              <p className="text-amber-800 text-sm mb-3">
                Plymouth&apos;s own report notes that Gaist survey methods &ldquo;do not conform to the same
                standards as applied to SCANNER&rdquo; and results are an interpretation to mirror SCANNER output.
                U-road RED percentages cannot be directly compared with classified-road SCANNER categories.
              </p>
              <p className="text-amber-800 text-sm">
                The council plans a further U-road condition survey in 2025/26. Until then, the most recent
                published figure remains 34.83% RED from 2022 — three years ago.
              </p>
            </div>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="We currently use a company called Gaist to undertake condition surveys on the unclassified network. They employ a method of visually analysing the network to establish a condition grade. We aim to undertake this survey of the whole unclassified network (excluding service lanes) every 3 years."
              source="Plymouth City Council Local Highways Maintenance Transparency Report (June 2025)"
            />
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing the
              condition of its roads. For Plymouth&apos;s unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — and was it one of the gap years?</li>
              <li>• If a third of U-roads were RED at the last survey, what was done about yours?</li>
              <li>• How does the council track deterioration on a road it surveys only every three years?</li>
              <li>• Does the Gaist visual method meet the same standard as SCANNER on classified roads?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed knowledge of a network it measures once every three years — on
              a methodology it admits differs from the SCANNER standard used elsewhere.
            </p>
          </div>
        </div>
      </section>

      {/* Resurfacing collapse */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Resurfacing collapsed while pothole fills peaked
          </h2>
          <p className="text-surface-600 mb-8">
            Preventative carriageway work and reactive pothole repairs moving in opposite directions
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden">
              <div className="bg-surface-100 px-6 py-3 border-b border-surface-200">
                <h3 className="font-semibold text-brand-900">Carriageway resurfacing (miles)</h3>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-surface-100">
                    <th className="px-6 py-2 text-left font-semibold">Year</th>
                    <th className="px-6 py-2 text-center font-semibold">Miles resurfaced</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-6 py-3 font-mono">2020/21</td>
                    <td className="px-6 py-3 text-center">6.03</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-6 py-3 font-mono">2021/22</td>
                    <td className="px-6 py-3 text-center">4.83</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-6 py-3 font-mono">2022/23</td>
                    <td className="px-6 py-3 text-center">4.45</td>
                  </tr>
                  <tr className="border-b border-surface-100 bg-rose-50">
                    <td className="px-6 py-3 font-mono font-bold">2023/24</td>
                    <td className="px-6 py-3 text-center font-bold text-rose-600">1.17</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-3 font-mono">2024/25</td>
                    <td className="px-6 py-3 text-center">1.80</td>
                  </tr>
                </tbody>
              </table>
              <p className="px-6 py-4 text-sm text-surface-600 border-t border-surface-100">
                An 80% drop from 2020/21 to 2023/24. On an 882km network, 1.17 miles of resurfacing treats
                roughly 0.2% of the carriageway in a year.
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden">
              <div className="bg-surface-100 px-6 py-3 border-b border-surface-200">
                <h3 className="font-semibold text-brand-900">Potholes filled (estimated)</h3>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-surface-100">
                    <th className="px-6 py-2 text-left font-semibold">Year</th>
                    <th className="px-6 py-2 text-center font-semibold">Potholes filled</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-6 py-3 font-mono">2020/21</td>
                    <td className="px-6 py-3 text-center">2,994</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-6 py-3 font-mono">2021/22</td>
                    <td className="px-6 py-3 text-center">2,300</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-6 py-3 font-mono">2022/23</td>
                    <td className="px-6 py-3 text-center">2,186</td>
                  </tr>
                  <tr className="border-b border-surface-100 bg-rose-50">
                    <td className="px-6 py-3 font-mono font-bold">2023/24</td>
                    <td className="px-6 py-3 text-center font-bold text-rose-600">3,218</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-3 font-mono">2024/25</td>
                    <td className="px-6 py-3 text-center">2,414</td>
                  </tr>
                </tbody>
              </table>
              <p className="px-6 py-4 text-sm text-surface-600 border-t border-surface-100">
                13,112 potholes filled over five years — about seven every day. Plymouth expects ~2,600 fills
                in 2025/26 based on the five-year average.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The counting caveat</h3>
              <p className="text-surface-600 text-sm">
                Plymouth states it is &ldquo;unable to individually identify numbers of potholes due to the way
                these works are recorded&rdquo; — figures are based on carriageway works orders completed. The true
                number of individual potholes treated may differ from the published totals.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Reactive pothole spend share</h3>
              <p className="text-amber-800 text-sm">
                Pothole repairs consumed 11–22% of reactive maintenance spend across the five years — peaking
                at 22% in 2020/21 and 21% in 2023/24, the same year resurfacing hit its low. The council
                classifies spray injection and traditional patching as preventative when treating emerging
                defects below investigatory criteria.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="As a means to undertake preventative repairs to emerging defects we have introduced spray injection and traditional patching to identify and repair defects which would otherwise not meet our defect investigatory criteria. This is aimed at reducing the number of emerging defects before they require a reactive intervention."
            source="Plymouth City Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Investigatory criteria and temporary repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Plymouth&apos;s repair threshold and temporary fixes
          </h2>
          <p className="text-surface-600 mb-8">
            What the council says about when it will — and will not — repair a pothole
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="We'll repair a pothole that's at least 40mm deep and over 300mm wide. The location of the pothole and the type of road help us decide when it will be repaired."
              source="Plymouth City Council — Report a pothole (plymouth.gov.uk/potholes)"
            />
            <QuoteCard
              quote="Sometimes our road maintenance contractor will need to carry out a temporary fix until permanent repairs can be arranged. This is often because road closures or other traffic management will be required and also because resources need to be allocated for repairs across the city."
              source="Plymouth City Council — Report a pothole (plymouth.gov.uk/potholes)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What the 40mm threshold means for claims</h3>
              <p className="text-surface-600 text-sm mb-3">
                Plymouth&apos;s investigatory criteria — 40mm deep and 300mm wide — define when the council will
                schedule a repair, not when it accepts liability for damage. A defect that caused your tyre or
                suspension damage may not yet have met the repair threshold when you hit it.
              </p>
              <p className="text-surface-600 text-sm">
                Prior reports of the same location — even for smaller defects — create a dated record of actual
                notice. Plymouth&apos;s claims page explicitly asks whether you reported the defect before claiming.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Temporary repairs as evidence</h3>
              <p className="text-amber-800 text-sm mb-3">
                Plymouth acknowledges temporary fixes are routine — often because permanent repairs need road
                closures or resource allocation across the city. Weathered edges around a patch, or a sequence
                of temporary fills at the same location, can indicate a structural surface failure the council
                knew about but had not permanently resolved.
              </p>
              <p className="text-amber-800 text-sm">
                The council states its goal is for &ldquo;the first repair being a permanent repair&rdquo; — yet
                publicly describes temporary fixes as a normal part of its process.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER means for claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against an AMBER-rated council with GREEN spend
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Plymouth invests heavily — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — projected capital spend more than triples DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 80% preventative share projected for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management strategy aligned to Well Managed Highways Infrastructure
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Active member of LCRIG, LGTAG and South West Highways Alliance
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Risk-based inspection regime with published Highways Inspection Manual
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence on well-maintained A-roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — 36.64% of U-roads in RED at last full survey
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 70% of network surveyed only every three years on non-SCANNER methodology
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> B/C amber roads up from 20.0% to 24.4% in 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Resurfacing fell 80% to 1.17 miles in 2023/24 while pothole fills peaked
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice scorecard from DfT
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits temporary pothole repairs are routine
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend but AMBER condition, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, Plymouth&apos;s online reporting) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class — on a U-road, the three-year survey gap is your strongest structural argument</li>
              <li>• Evidence of temporary repairs at the same location — council-admitted practice</li>
              <li>• Whether the defect was below Plymouth&apos;s 40mm investigatory threshold when you reported it</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Plymouth&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to Plymouth first
          </h2>
          <p className="text-surface-600 mb-6">
            Plymouth&apos;s claims guidance states you should have reported the defect before submitting a claim.
            Reporting through the council creates a dated record — useful evidence if the pothole was reported
            before your incident, or if the council failed to repair it within a reasonable time. Plymouth repairs
            potholes at least 40mm deep and over 300mm wide; keep your reference number regardless of size.
          </p>
          <a
            href="https://www.plymouth.gov.uk/potholes"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a pothole to Plymouth City Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            If the pothole is on the A38 Parkway, report it to National Highways instead. Keep your reference
            number and any confirmation emails.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Plymouth?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded AMBER council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No three-year U-road survey gap argument</li>
                <li>• No 36.64% RED U-road data cited</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 36.64% U-road RED condition documented</li>
                <li>✅ Three-year survey gap and Gaist methodology argued</li>
                <li>✅ Resurfacing collapse and 3,218 pothole peak cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Plymouth</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=plymouth"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my Plymouth claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Plymouth spends three times its DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but your claim turns on whether the specific defect that
                damaged your vehicle was reasonably inspected and repaired under Section 58 — not on aggregate
                spending. Plymouth is AMBER overall because road condition is AMBER: B and C roads lost five
                percentage points of green condition in 2024, and over a third of unclassified roads were in RED
                at the last comparable survey.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 621.67km — 70% of Plymouth&apos;s 882km network — and Plymouth&apos;s
                own Gaist surveys found 36.64% in RED condition in 2020 and 34.83% in 2022. The council surveys
                U-roads only every three years, so there is no network-level condition data for 2021, 2023 or
                2024. That survey gap is a structural argument under Section 58.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Plymouth&apos;s 40mm investigatory threshold affect my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It defines when Plymouth will repair a pothole — at least 40mm deep and over 300mm wide — but
                it does not define when the council is liable for damage. A defect below the repair threshold can
                still have caused your damage, and prior reports of the same location create a record of actual
                notice regardless of whether the pothole yet met investigatory criteria.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Plymouth says temporary pothole repairs are common — does that help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It can. Plymouth publicly acknowledges that its contractor sometimes carries out temporary fixes
                until permanent repairs can be arranged. A temporary patch on a road already in RED condition at
                the last survey suggests the underlying surface failure was known — and that reactive patching
                did not restore the carriageway to a maintainable standard.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why did pothole repairs peak at 3,218 in 2023/24?
              </h3>
              <p className="text-surface-600 text-sm">
                Plymouth filled 3,218 potholes in 2023/24 — the highest in five years — the same year carriageway
                resurfacing fell to just 1.17 miles, down from 6.03 miles in 2020/21. When preventative
                resurfacing collapses and reactive pothole filling spikes, defects are forming faster than
                structural renewal can keep pace — exactly the pattern where prior reports and photographic
                evidence decide claims.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                The A38 is not Plymouth&apos;s responsibility — who do I claim against?
              </h3>
              <p className="text-surface-600 text-sm">
                Plymouth Highways does not manage the A38 Parkway or many of its slip-roads — National Highways
                does. If your damage was on the A38, report it to National Highways and pursue your claim against
                them. Plymouth&apos;s transparency report covers HMPE (Highways Maintainable at Public Expense) only,
                excluding trunk roads.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Do I need to report the pothole before claiming?
              </h3>
              <p className="text-surface-600 text-sm">
                Plymouth&apos;s own claims guidance states you should have reported the defect before submitting a
                claim. Reporting creates a dated council record — useful if the pothole was reported before your
                incident, or if the council failed to repair it within a reasonable time. Keep your reference
                number and any confirmation emails.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.plymouth.gov.uk/local-highways-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Plymouth City Council Local Highways Maintenance Transparency Report (June 2025)
            </a>{' '}
            |{' '}
            <a
              href="https://www.plymouth.gov.uk/potholes"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Plymouth City Council — Report a pothole
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
